import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateOrUpdateTailNumberOverride } from "@/queries/landing";
import { PricedFlight } from "@/types/landing";
import { Plus } from "lucide-react";
import React, { useState } from "react";

interface TailNumberOverrideProps {
  flight: PricedFlight;
}

export function TailNumberOverrideDialog({ flight }: TailNumberOverrideProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [tailNumberInput, setTailNumberInput] = useState("");

  const tailNumberMutation = useCreateOrUpdateTailNumberOverride();

  const handleOpenModal = () => {
    setTailNumberInput("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tailNumberInput.trim()) return;

    try {
      await tailNumberMutation.mutateAsync({
        priced_flight_id: flight.id,
        tail_number: tailNumberInput.trim(),
      });
      setModalOpen(false);
      setTailNumberInput("");
    } catch (error) {
      console.error("Failed to update tail number:", error);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTailNumberInput("");
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={handleOpenModal}>
        <Plus className="h-1 w-1" />
        Add
      </Button>

      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-[425px] bg-slate-900 text-slate-200">
          <DialogHeader>
            <DialogTitle>Add Tail Number</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="flight-info" className="text-sm font-medium">
                Flight Information
              </Label>
              <div className="text-sm text-gray-400 bg-slate-800 p-3 rounded">
                <div>
                  <strong>Flight:</strong>{" "}
                  {flight.flight_number_display || "GA Flight"}
                </div>
                <div>
                  <strong>Route:</strong> {flight.departure_airport_code} →{" "}
                  {flight.arrival_airport_code}
                </div>
                <div>
                  <strong>Time:</strong> {flight.departure_time_display} -{" "}
                  {flight.arrival_time_display}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tail-number" className="text-sm font-medium">
                Tail Number
              </Label>
              <Input
                id="tail-number"
                type="text"
                placeholder="Enter tail number (e.g., N12345)"
                value={tailNumberInput}
                onChange={(e) => setTailNumberInput(e.target.value)}
                className="bg-slate-800 border-gray-600 focus:border-gray-500"
                required
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="submit"
                variant="default"
                disabled={
                  tailNumberMutation.isPending || !tailNumberInput.trim()
                }
              >
                {tailNumberMutation.isPending ? "Saving..." : "Add"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
